import { Box, Button, MenuItem, TextField } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import DataGridTamplet from "../Helper/TableTemplet/DataGridTamplet";
import { activeUser, chargeCustomer, checkIsExistsUserInStripe, createInvoice, deleteUserDB, getUsersList } from "./UsersUtils";
import PhoneInputField2 from "../Helper/PhonePrefixForm/PhoneInputField2";
import userRoles from "../../configs/userRolesConfigs";
import { UserRolesEnum } from "../../Models/user";

const Users = () => {
  const dispatch = useDispatch();
  let users = useSelector((state: any) => state.user.user_list);
  const userRole = useSelector((state: any) => state.general.userRol);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [searchField, setSearchField] = useState("first_name");
  const [filtersValues, setFiltersValues] = useState({ fieldText: "" });
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    { field: 'first_name', headerName: 'First Name', sortable: true, width: 150 },
    { field: 'last_name', headerName: 'Last Name', sortable: true, width: 150 },
    { field: 'email', headerName: 'Email', sortable: true, width: 200 },
    {
      field: 'role', headerName: 'Role', sortable: true, width: 100,
      valueGetter: (params) => { return UserRolesEnum[params.value as string] }
    },
    { field: 'phone', headerName: 'Phone', sortable: true, width: 100 },
    {
      field: 'amount_to_charge', headerName: 'Amount to charge', sortable: true, width: 165,
      valueFormatter: ({ value }) => value && value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    { field: 'last_date_invoice', headerName: 'Last date invoice', sortable: true, width: 165 },
  ];

  const cfoColumns = [
    ...columns,
    {
      field: 'charges', headerName: 'Charges', width: 220, sortable: false, renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ transform: "scale(0.9)" }}>
          {params.row.role === 'account' && <Button
            variant="outlined"
            className="mr-10p yellow-outlined-btn"
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => createBillingInvoice(params.id as number)}
          >
            Create Invoice
          </Button>}
          {params.row.role === 'account' && <Button
            variant="outlined"
            className="yellow-outlined-btn"
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => chargeCustomer(dispatch, params.id as number)}
          >
            Charge
          </Button>}
        </Box>
      )
    }
  ];
  
  const adminColumns = [
    ...cfoColumns,
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false, renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ transform: "scale(0.9)" }}>
          <Button
            variant="outlined"
            className="mr-10p blue-outlined-btn"
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => navigateUserForm(params.id as number)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            disabled={params.row.status !== "Y"}
            hidden={params.row.status !== "Y"}
            tabIndex={params.hasFocus ? 0 : -1}
            className={`red-outlined-btn ${params.row.status === "N" ? 'd-none' : ''}`}
            onClick={() => openConfirmDelete(params.id as number)}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            disabled={params.row.status !== "N"}
            className={`green-outlined-btn ${params.row.status === "Y" ? 'd-none' : ''}`}
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => activeUser(dispatch, params.row.id as number)}
          >
            Active
          </Button>
        </Box>
      )
    },
  ];

  useEffect(() => {
    getUsersList(dispatch);
  }, []);

  const deleteUser = () => {
    deleteUserDB(dispatch, userIdToDelete);
    setUserIdToDelete(null);
  };

  const navigateUserForm = (userId: number) => {
    navigate(`/layout/administrator/users/details/${userId}/${1}`);
  };

  const navigateToUserForm = () => {
    navigate('/layout/administrator/users/create/0');
  };

  const navigatePaymentMethods = async (userId: number) => {
    await checkIsExistsUserInStripe(dispatch, userId);
    navigate(`/layout/settings/payment-methods/${userId}`);
  };

  const openConfirmDelete = (userId: number) => {
    setConfirmOpen(true);
    setUserIdToDelete(userId);
  };

  const createBillingInvoice = (userId: number) => {
    createInvoice(dispatch, userId);
  };

  const search = (list, inputValue) => {
    return automaticSearch(list, inputValue);
  };

  const automaticSearch = (list, value) => {
    if (value === "") return list;

    return list.filter((item) => {
      const fieldValue = item[searchField];
      return fieldValue !== null && fieldValue.toString().toLowerCase().includes(value.toLowerCase());
    });
  };

  return (
    <div>
      <h2>Users</h2>
      <Box display="flex" alignItems="center" mb={2}>
        <TextField
          select
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
          label="Search Field"
          variant="outlined"
          sx={{ marginRight: 2 }}
        >
          <MenuItem value="first_name">First Name</MenuItem>
          <MenuItem value="last_name">Last Name</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="role">Role</MenuItem>
          <MenuItem value="phone">Phone</MenuItem>
        </TextField>
        <TextField
          variant="outlined"
          label={`Search by ${searchField.replace('_', ' ')}`}
          value={filtersValues.fieldText}
          onChange={(e) => setFiltersValues({ ...filtersValues, fieldText: e.target.value })}
        />
      </Box>
      <DataGridTamplet
        columns={userRole === userRoles.admin ? adminColumns : userRole === userRoles.cfo ? cfoColumns : columns}
        rows={search(users, filtersValues.fieldText)}
      >
        {userRole === userRoles.admin && <Button
          onClick={navigateToUserForm}
          className="blue-btn"
        >
          Create User
        </Button>}
      </DataGridTamplet>
      <ConfirmDialog
        title="Delete User?"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteUser}
      >
        Are you sure you want to delete this user?
      </ConfirmDialog>
    </div>
  )
};

export default Users;